import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'

import styles from './style.module.scss'
import { Col, Container, Form, Row } from 'react-bootstrap'
import CustomSelect from '@shared/components/customSelect'

import { GENDERS, ROLES } from '@shared-submodule/sm-constants'
import { useQuery } from '@apollo/client'
import { COUNTRIES_LIST } from '@shared-submodule/sm-graphql/teams/teams.query'
import ToggleSwitch from '@shared/components/ToggleSwitch'
import useWindowSize from '@shared/hooks/windowSize'
import { getPlayerRole } from '@shared-submodule/utils'

export default function PlayersFilter({ defaultValues, handleFilterChange, handleSearchChange }) {
  const [width] = useWindowSize()
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')
  const [internationalOnly, setInternationalOnly] = useState(false)
  const [activePlayers, setActivePlayers] = useState(false)
  const [values, setValues] = useState({
    sSearch: defaultValues?.sSearch,
    startsWith: defaultValues?.startsWith,
    gender: { sLabel: defaultValues?.gender },
    nationality: { sLabel: defaultValues?.nationality },
    international: defaultValues?.international,
    role: { sLabel: defaultValues?.role, sValue: getPlayerRole(defaultValues?.role) },
    activePlayers: defaultValues?.activePlayers
  })
  const [countries, setCountries] = useState([])
  const [selectedAlpha, setSelectedAlpha] = useState(() => {
    return defaultValues?.sSearch ? '' : defaultValues?.startsWith || 'A'
  })

  useEffect(() => {
    setInternationalOnly(defaultValues?.international === 'true')
    setActivePlayers(defaultValues?.activePlayers === 'true')
    setSelectedAlpha(defaultValues?.sSearch ? '' : defaultValues?.startsWith || 'A')
  }, [defaultValues])

  useQuery(COUNTRIES_LIST, {
    onCompleted: (data) => {
      setCountries(data?.listTeamsCountry.map(country => ({ sLabel: country, sValue: country })) || [])
    }
  })

  function onSearchChange({ target }) {
    const val = { ...values, sSearch: target.value.trim()?.length > 0 ? target.value : target.value.trim() }
    setValues(val)
    if (target.value.trim()?.length > 0) {
      setSelectedAlpha('')
    } else {
      setSelectedAlpha('A')
    }
    handleSearchChange(target.value.trim()?.length > 0 ? target.value : target.value.trim())
  }

  function onFilterChange(e, type) {
    if (type === 'startsWith') {
      const val = { ...values, [type]: e }
      delete val.sSearch
      setValues(val)
    } else {
      setValues(v => ({ ...v, [type]: e }))
    }
    handleFilterChange(e, type)
  }

  function onFilterChangeWrapper(type) { return function(e) { onFilterChange(e, type) } }

  const handleChange = (comp) => {
    if (comp === 'international') {
      setInternationalOnly(!internationalOnly)
      onFilterChange(!internationalOnly, 'international')
    } else {
      setActivePlayers(!activePlayers)
      onFilterChange(!activePlayers, 'activePlayers')
    }
  }

  return (
    <div className={`${styles.teamsFilter} pb-2`}>
      <Container className='mt-3'>
        <Row className='mt-2'>
          <Col xs={12} className={`${styles.searchBox} mb-1`}>
            <Form.Control
              type="input"
              placeholder={'Thinking of a Player ?  Search them right away.'}
              value={values?.sSearch || ''}
              className={`${styles?.search} d-flex`}
              onChange={onSearchChange}
            />
          </Col>
        </Row>
        <Row className={`${width > 767 ? 'mt-2' : ''}`}>
          <Col xs={8} className={`${width > 767 ? 'mt-3' : 'mt-2'} d-md-none`}>
            <span className={styles.caption}>Filter by</span>
          </Col>
          <div className={`${styles.clear} ${styles.clearFilter} small-text pb-1 d-md-none mt-2`} onClick={(e) => {
            setValues(null)
            handleFilterChange(null, 'clear')
          }} >
            Clear All
          </div>
          <Col xs={12} sm={5} md={2} className='my-md-0 my-sm-2 my-3'>
            <CustomSelect
              isSearchable
              isStatic
              options={countries}
              value={values?.nationality}
              placeholder={'Choose Country'}
              labelKey="sLabel"
              valueKey="sValue"
              isMedium={true}
              onChange={onFilterChangeWrapper('nationality')}
            />
          </Col>
          <Col xs={12} sm={5} md={2} className={`${width < 768 ? styles.dashedBorder + ' w-50 mb-3' : ''} my-md-0 my-sm-2`}>
            <CustomSelect
              options={GENDERS}
              value={values?.gender}
              placeholder={'Choose Gender'}
              labelKey="sLabel"
              valueKey="sValue"
              isMedium={true}
              onChange={onFilterChangeWrapper('gender')}
            />
          </Col>
          <Col xs={12} sm={5} md={2} className={`${width < 768 ? styles.dashedBorder + ' w-50 mb-3' : ''} my-md-0 my-sm-2`}>
            <CustomSelect
              options={ROLES}
              value={values?.role}
              placeholder={'Choose Role'}
              labelKey="sValue"
              valueKey="sLabel"
              isMedium={true}
              onChange={onFilterChangeWrapper('role')}
            />
          </Col>
          <Col xs={12} sm={5} md={2} className={`${styles.internationalPlayers} d-flex align-items-center`}>
            <ToggleSwitch
              title={'International Only'}
              handleChange={handleChange}
              component='international'
              isChecked={internationalOnly}
            />
          </Col>
          <Col xs={12} sm={5} md={2} className={`${styles.activePlayers} d-flex align-items-center`}>
            <ToggleSwitch
              title={'Active Players'}
              component='activePlayers'
              handleChange={handleChange}
              isChecked={activePlayers}
            />
          </Col>
          <Col md={1} sm={2} className={`${styles.clearFilter} align-items-center d-md-flex d-none`}>
            <div className={`${styles.clear} small-text pb-1`} onClick={(e) => {
              setValues(null)
              handleFilterChange(null, 'clear')
            }} >
              Clear All
            </div>
          </Col>
        </Row>
        <Row className='pt-3 lh-15'>
          {
            letters?.map((ele, i) => {
              return (
                <span className={`${selectedAlpha === ele && styles.active} ${styles.alpha} mr-01 w-0 cursor-pointer`} key={ele} onClick={() => {
                  setSelectedAlpha(ele)
                  onFilterChange(ele, 'startsWith')
                }
                }>
                  {ele}
                </span>
              )
            })
          }
        </Row>
      </Container>
    </div>
  )
}

PlayersFilter.propTypes = {
  handleFilterChange: PropTypes.func,
  handleSearchChange: PropTypes.func,
  defaultValues: PropTypes.object
}
